import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import MainLayout from 'components/layout/MainLayout';
import ProgressionBar, {
  CurrentUserProgressionBar,
} from 'components/monthly-giveaway/ProgressionBar';
import RoundedButton from 'components/RoundedButton';
import { useAuthRedirectEffect } from 'hooks/account/useAuthRedirect';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { runningLocally } from 'utils/constants';

const requireAdmin = (component: React.FC) => {
  return observer(() => {
    const userStore = useUserStore();
    useAuthRedirectEffect('/design-system');
    if (
      !runningLocally &&
      (!userStore.initialized ||
        !(
          userStore.currentUser?.role === 'ADMIN' ||
          userStore.currentUser?.role === 'OPERATOR'
        ))
    ) {
      return "Sorry, you aren't allowed to view this page";
    }

    const Component = component;
    return <Component />;
  });
};

const DesignSystem = () => {
  return (
    <MainLayout limitItemWidths>
      <style>
        {`
        .hackHeadingTextAlign h1,
        .hackHeadingTextAlign h2,
        .hackHeadingTextAlign h3,
        .hackHeadingTextAlign h4 {
          /* The global styles of the site force all headings to be centered. There is no elegant way to override
          this without breaking the existing site. Additionally, responsive layouts may require headings to be centered
          on mobile, but not desktop, etc. Individual pages have to provide their own alignment to override it
          appropriately. The design system page does it here:*/
          text-align: left;
        }
        `}
      </style>
      <div className="aboutUsTypographyReset hackHeadingTextAlign">
        <AboutUsModule background="grey">
          <AboutUsSiteContainer>
            <h1 className="aboutUsTypographyHeaderXlargeOnWhite">
              The DAYMADE design system
            </h1>
            <p>
              This page showcases the various styles and components use around
              the site. It is a work in progress. Many of the pages to not (yet)
              use the design system
            </p>
            <h2 className="aboutUsTypographyHeaderXlarge">Typography</h2>
            <p>
              This page demonstrates various parts of the Daymade design system.
              It is a work in progress.
            </p>
            <h3 className="aboutUsTypographyHeaderXlargeOnTeal">Heading XL</h3>
            <p>
              Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
              eiusmod tempor incididunt ut labore et dolore magna aliqua.
            </p>

            <h3 className="aboutUsTypographyHeaderXlargeOnWhite">
              Heading XL (with underline)
            </h3>
            <p>
              Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
              eiusmod tempor incididunt ut labore et dolore magna aliqua.
            </p>

            <h3 className="aboutUsTypographyHeaderXlargeOnWhite aboutUsTypographyAltUnderlineColour">
              Heading XL (with underline, alt colour)
            </h3>
            <p>
              Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
              eiusmod tempor incididunt ut labore et dolore magna aliqua.
            </p>

            <h3
              className="aboutUsTypographyHeaderXlargeOnWhite"
              style={{ width: '50%' }}
              data-text="Heading XL (only underline last line)"
            >
              Heading XL (only underline last line)
            </h3>
            <p>
              Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
              eiusmod tempor incididunt ut labore et dolore magna aliqua.
            </p>

            <h3 className="aboutUsTypographyHeaderLarge">Heading Large</h3>
            <p>
              Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
              eiusmod tempor incididunt ut labore et dolore magna aliqua.
            </p>

            <h3 className="aboutUsTypographyHeaderMedium">Heading Medium</h3>
            <p>
              Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
              eiusmod tempor incididunt ut labore et dolore magna aliqua.
            </p>

            <p>
              Body text (default) - Lorem ipsum dolor sit amet, consectetur
              adipiscing elit, sed do eiusmod tempor incididunt ut labore et
              dolore magna aliqua.
            </p>
            <p className="aboutUsTypographyBodySmall">
              Body text small - Lorem ipsum dolor sit amet, consectetur
              adipiscing elit, sed do eiusmod tempor incididunt ut labore et
              dolore magna aliqua.
            </p>
            <p className="aboutUsTypographyCaption">
              Caption - Lorem ipsum dolor sit amet, consectetur adipiscing elit,
              sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
            </p>
          </AboutUsSiteContainer>
        </AboutUsModule>
        <AboutUsModule background={'white'}>
          <AboutUsSiteContainer>
            <h2 className="aboutUsTypographyHeaderXlarge">Components</h2>
          </AboutUsSiteContainer>
        </AboutUsModule>
        <AboutUsModule background={'tealGradient'}>
          <AboutUsSiteContainer>
            <h3 className="aboutUsTypographyHeaderLarge">
              Modules and containers
            </h3>
            <p>
              Modules control the horizontal coloured bands of content across a
              page. The module provides the vertical padding within the coloured
              band.
            </p>
            <p>
              Each module usually has a container, which centers and pads the
              content to the middle of the screen.
            </p>
          </AboutUsSiteContainer>
        </AboutUsModule>
        <AboutUsModule background={'white'} withNoPadding>
          <div
            style={{
              textAlign: 'center',
              padding: 0,
              border: '5px dotted blue',
            }}
          >
            <p style={{ width: '50%', margin: 'auto' }}>
              However, some components use the full width of the screen. For
              example, this component has a dotted border all of the way around
              it touching the edges of the screen. These components need to
              provide their own padding or centre their content. A component
              that may need to set withNoPadding on its Module to remove the
              vertical spacing between Modules.
            </p>
          </div>
        </AboutUsModule>
        <AboutUsModule background="grey">
          <AboutUsSiteContainer>
            <p>
              Both modules will receive vertical spacing when two different
              coloured modules are placed next to each other.
            </p>
          </AboutUsSiteContainer>
        </AboutUsModule>
        <AboutUsModule background="grey">
          <AboutUsSiteContainer>
            <p>
              However, that space is not present if two consecutive modules are
              the same colour. This is to reduce the amount of blank space that
              would otherwise appear. The following/succeeding module has its
              top spacing removed.
            </p>
          </AboutUsSiteContainer>
        </AboutUsModule>
        <AboutUsModule background="white">
          <AboutUsSiteContainer>
            <h3 className="aboutUsTypographyHeaderLarge">Progression bar</h3>
            <p>
              The progression bar, as used on the Monthly 10k. It is designed to
              fill its container horiziontally. Therefore, its parent container
              must provide any spacing and size the Progression bar
              appropriately.
              <br />
              <br />
            </p>
            <div style={{ width: '50%' }}>
              <p>This bar uses the currently logged in user's entries</p>
              <CurrentUserProgressionBar />
              <br />
            </div>
            <p>
              The following bars show how the component fills containers of
              different widths
            </p>
            <div style={{ width: '50%' }}>
              <ProgressionBar
                shortMonthName="Sept"
                weekMarkers={[
                  { dayNumber: 1, earnings: 5, isMidweek: true },
                  { dayNumber: 8, earnings: 5, isMidweek: true },
                  { dayNumber: 15, earnings: 5, isMidweek: true },
                  { dayNumber: 22, earnings: 5 },
                  { dayNumber: 29, earnings: 0 },
                ]}
              />
              <br />
            </div>
            <div style={{ width: '40%' }}>
              <ProgressionBar
                shortMonthName="Mar"
                weekMarkers={[
                  { dayNumber: 1, earnings: 5, isMidweek: true },
                  { dayNumber: 8, earnings: 5, isMidweek: true },
                  { dayNumber: 15, earnings: 5, isMidweek: true },
                  { dayNumber: 22, earnings: 5 },
                  { dayNumber: 29, earnings: 0 },
                ]}
              />
              <br />
            </div>
            <div style={{ width: '40%' }}>
              <ProgressionBar
                shortMonthName="Jan"
                weekMarkers={[
                  { dayNumber: 1, earnings: 0 },
                  { dayNumber: 8, earnings: 5, isMidweek: true },
                  { dayNumber: 15, earnings: 5, isMidweek: true },
                  { dayNumber: 22, earnings: 5, isMidweek: true },
                  { dayNumber: 29, earnings: 0 },
                ]}
              />
              <br />
            </div>
            <div style={{ width: '30%' }}>
              <ProgressionBar
                shortMonthName="Dec"
                weekMarkers={[
                  { dayNumber: 1, earnings: 5, isMidweek: true },
                  { dayNumber: 8, earnings: 5, isMidweek: true },
                  { dayNumber: 15, earnings: 5, isMidweek: true },
                  { dayNumber: 22, earnings: 0 },
                  { dayNumber: 29, earnings: 0 },
                ]}
              />
              <br />
            </div>
            <h3 className="aboutUsTypographyHeaderLarge">Button</h3>
            <p>
              These are design system buttons. They do not currently fill their
              container. Their vertical height may change depending on the
              container that they appear in, which then makes the button text
              appear non-centered.
            </p>
            <div style={{ maxWidth: 300 }}>
              <RoundedButton rebrandDm2025>Button text</RoundedButton> <br />
              <br />
              <RoundedButton rebrandDm2025 secondary>
                Button text
              </RoundedButton>{' '}
              <br />
              <br />
              <RoundedButton rebrandDm2025 loading>
                Button text
              </RoundedButton>{' '}
              <br />
              <br />
              <RoundedButton rebrandDm2025 disabled>
                Button text
              </RoundedButton>
            </div>
            <p>
              This is how buttons behave in a flex container. A secondary button
              has a border, but a primary button does not. This causes the
              button text to be centered incorrectly. Additionally, the
              background colour is set via inline styles, but there is also a
              hover colour. This makes adding a border to match the heights
              tricky. The component should be rewritten to just use CSS and no
              inline-styles.
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 24,
              }}
            >
              <RoundedButton rebrandDm2025>Button text</RoundedButton>
              <RoundedButton rebrandDm2025 secondary>
                Button text
              </RoundedButton>
              <RoundedButton rebrandDm2025 loading>
                Button text
              </RoundedButton>
              <RoundedButton rebrandDm2025 disabled>
                Button text
              </RoundedButton>
            </div>
          </AboutUsSiteContainer>
        </AboutUsModule>
        <AboutUsModule background="white">
          <AboutUsSiteContainer>
            <h3 className="aboutUsTypographyHeaderLarge">Other components</h3>
            <p>
              We should add other components here. When this page starts to get
              too big, we should consider splitting it up.
            </p>
          </AboutUsSiteContainer>
        </AboutUsModule>
      </div>
    </MainLayout>
  );
};

export default requireAdmin(DesignSystem);
